import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import { activateKioskMode } from '../lib/kioskMode';
import { capturePairTokenFromUrl } from '../lib/devicePairing';
import DeviceUnauthorized from './DeviceUnauthorized';
import { 
  MapPin, 
  Building, 
  Calendar, 
  Clock, 
  Users,
  RefreshCw,
  GraduationCap,
  DoorOpen,
  Briefcase,
  ChevronUp,
  ChevronDown,
  Pause,
  Play,
  Maximize,
  Minimize,
} from 'lucide-react';
import { format, addDays, startOfWeek, getISOWeek, getISOWeekYear } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { useCompanyBranding, resolveLogoUrl } from '../hooks/useCompanyBranding';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Activity type colors and icons
const ACTIVITY_STYLES = {
  berufsschule: { color: '#3B82F6', label: 'Berufsschule', icon: GraduationCap },
  praktikum: { color: '#10B981', label: 'Praktikum', icon: Briefcase },
  exkursion: { color: '#F59E0B', label: 'Exkursion', icon: MapPin },
  pruefung: { color: '#EF4444', label: 'Prüfung', icon: Calendar },
  sonstiges: { color: '#6B7280', label: 'Sonstiges', icon: Calendar },
};

// Isolierte Uhr — hält ihren eigenen 1s-Timer, damit der Sekundentakt NUR die
// Uhr neu rendert und nicht das komplette (große) TV-Grid → verhindert Ruckeln.
const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span>
      {now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </span>
  );
};

export default function ShiftsTVView() {
  const { locationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceUnauthorized, setDeviceUnauthorized] = useState(false);
  // Firmen-Branding (Logo) — central via useCompanyBranding mit 1h-Cache.
  const branding = useCompanyBranding();
  const companyLogo = resolveLogoUrl(branding.logo_url);
  
  // Auto-Scroll State
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [scrollSpeed, setScrollSpeed] = useState(() => {
    const stored = localStorage.getItem('tv_scroll_speed');
    return stored ? parseInt(stored) : 30;
  });
  const [scrollDirection, setScrollDirection] = useState(1); // 1 = runter, -1 = hoch
  // Modus: 'loop' = nach unten springt zurück zum Anfang; 'pingpong' = wechselt Richtung
  const [scrollMode, setScrollMode] = useState(() => localStorage.getItem('tv_scroll_mode') || 'loop');
  const [isPausedAtEnd, setIsPausedAtEnd] = useState(false);
  // Display-Size (S/M/L/XL/AUTO): vergroessert den Schedule-Grid via CSS-`zoom`,
  // damit grosse Fernseher aus 3-5m Distanz lesbar bleiben. 'auto' = Auto-Fit:
  // alle Massnahmen-Reihen passen ohne Scrollen in den Viewport.
  const [displaySize, setDisplaySize] = useState(() => localStorage.getItem('tv_display_size') || 'auto');
  // Auto-Fit-Zoom: wird in useLayoutEffect berechnet, sodass scrollHeight==clientHeight.
  const [autoZoom, setAutoZoom] = useState(1);
  // Spalten-Modus: '1' oder '2'. Bei vielen Massnahmen kann der User auf 2 Spalten
  // umschalten — die Massnahmen werden gleichmaessig auf links/rechts verteilt,
  // pro Spalte ein eigenes Wochengrid. Auto-Fit rechnet dann pro Spalte separat.
  const [columnsMode, setColumnsMode] = useState(() => localStorage.getItem('tv_columns_mode') || '1');
  // Footer-Auto-Hide: blendet die Bedienleiste nach 10 s Inaktivitaet aus,
  // damit der TV-Monitor sauberer wirkt. Maus-/Touch-Bewegung blendet sie wieder ein.
  const [footerVisible, setFooterVisible] = useState(true);
  const footerHideTimerRef = useRef(null);
  // Vollbild-Status: spiegelt `document.fullscreenElement` zur Icon-Anzeige.
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Burn-In-Schutz: alle 5 Min minimaler Pixel-Versatz, damit OLED-/Plasma-TVs
  // bei stundenlanger Anzeige keine eingebrannten Spaltenlinien bekommen.
  const [burnInOffset, setBurnInOffset] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef(null);
  // Akkumulierte Float-Scroll-Position (sonst verschluckt der Browser
  // bei kleinen Geschwindigkeiten <30 den Sub-Pixel-Inkrement).
  const scrollPositionRef = useRef(0);
  const rafRef = useRef(null);
  const lastFrameTsRef = useRef(0);
  // Mutable Spiegel der Scroll-Einstellungen — so muss die rAF-Schleife bei
  // Speed-/Modus-/Richtungswechsel NICHT neu gestartet werden (kein Ruckeln).
  const scrollSpeedRef = useRef(30);
  const scrollModeRef = useRef('loop');
  const scrollDirectionRef = useRef(1);
  const isPausedAtEndRef = useRef(false);
  // Live-Sync: zuletzt bekannte TV-Version; wird alle 10 s gepollt, bei
  // Änderung lädt der TV-Monitor die vollen Daten neu — ohne Full-Reload.
  const lastVersionRef = useRef(null);
  const [liveSyncAt, setLiveSyncAt] = useState(null);

  // Initial load + Live-Sync-Poll alle 10 s (Version-Check → vollständiger
  // Reload nur bei tatsächlicher Änderung). Fallback-Full-Refresh alle 5 Min.
  useEffect(() => {
    loadData();

    const checkVersion = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/einsatzplan/tv/${locationId}/version`);
        setLiveSyncAt(new Date());
        if (lastVersionRef.current === null) {
          lastVersionRef.current = data.version;
          return;
        }
        if (data.version !== lastVersionRef.current) {
          lastVersionRef.current = data.version;
          await loadData();
        }
      } catch {
        /* still online next poll */
      }
    };

    const versionTimer = setInterval(checkVersion, 10 * 1000);
    const fallbackTimer = setInterval(loadData, 5 * 60 * 1000);
    return () => {
      clearInterval(versionTimer);
      clearInterval(fallbackTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  // Refs mit aktuellen Settings synchron halten (ohne die rAF-Schleife neu zu starten).
  useEffect(() => { scrollSpeedRef.current = scrollSpeed; }, [scrollSpeed]);
  useEffect(() => { scrollModeRef.current = scrollMode; }, [scrollMode]);
  useEffect(() => { scrollDirectionRef.current = scrollDirection; }, [scrollDirection]);
  useEffect(() => { isPausedAtEndRef.current = isPausedAtEnd; }, [isPausedAtEnd]);

  // Auto-Scroll Logic — läuft über requestAnimationFrame (synchron zum Bildaufbau)
  // statt setInterval. Bewegung ist zeit-basiert (Δt), damit sie auf langsameren
  // TV-Browsern gleich schnell UND gleichmäßig (ruckelfrei) bleibt.
  useEffect(() => {
    if (!autoScrollEnabled || !scrollContainerRef.current) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    // Float-Position mit aktuellem scrollTop synchronisieren (kein Sprung nach
    // manuellem Scrollen).
    scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    lastFrameTsRef.current = 0;

    const step = (ts) => {
      const container = scrollContainerRef.current;
      if (!container) { rafRef.current = requestAnimationFrame(step); return; }

      // Δt in Sekunden seit letztem Frame (bei erstem Frame 0).
      const dt = lastFrameTsRef.current ? (ts - lastFrameTsRef.current) / 1000 : 0;
      lastFrameTsRef.current = ts;

      if (isPausedAtEndRef.current) { rafRef.current = requestAnimationFrame(step); return; }

      const maxScroll = container.scrollHeight - container.clientHeight;
      if (maxScroll <= 5) { rafRef.current = requestAnimationFrame(step); return; }

      const currentScroll = container.scrollTop;
      // Pixel pro Sekunde = scrollSpeed; mit Δt multipliziert → frameratenunabhängig.
      const pxThisFrame = scrollSpeedRef.current * dt;

      if (scrollModeRef.current === 'loop') {
        if (currentScroll >= maxScroll - 2) {
          setIsPausedAtEnd(true);
          isPausedAtEndRef.current = true;
          setTimeout(() => {
            if (!scrollContainerRef.current) return;
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => {
              if (!scrollContainerRef.current) return;
              scrollPositionRef.current = scrollContainerRef.current.scrollTop;
              setIsPausedAtEnd(false);
              isPausedAtEndRef.current = false;
            }, 3000);
          }, 1500);
          rafRef.current = requestAnimationFrame(step);
          return;
        }
        scrollPositionRef.current += pxThisFrame;
        container.scrollTop = scrollPositionRef.current;
      } else {
        if (currentScroll >= maxScroll - 5 && scrollDirectionRef.current === 1) {
          scrollDirectionRef.current = -1;
          setScrollDirection(-1);
        } else if (currentScroll <= 5 && scrollDirectionRef.current === -1) {
          scrollDirectionRef.current = 1;
          setScrollDirection(1);
        }
        scrollPositionRef.current += scrollDirectionRef.current * pxThisFrame;
        container.scrollTop = scrollPositionRef.current;
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScrollEnabled]);

  // Burn-In-Schutz: alle 5 Minuten den gesamten Inhalt um zufällige 0-2px
  // verschieben. Verhindert auf OLED-/Plasma-TVs eingebrannte Spaltenlinien
  // bei stundenlanger statischer Anzeige.
  useEffect(() => {
    const shift = () => {
      setBurnInOffset({
        x: Math.floor(Math.random() * 5) - 2, // -2..+2
        y: Math.floor(Math.random() * 5) - 2,
      });
    };
    const timer = setInterval(shift, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // Kiosk-Lock: dieser Tab ist jetzt als TV-Geraet markiert. Solange dieser
  // Tab offen ist, leiten unbekannte URLs (inkl. /login) automatisch hierher
  // zurueck — verhindert, dass Gaeste am TV die Login-Seite ueber die
  // Adressleiste entdecken.
  useEffect(() => {
    activateKioskMode(window.location.pathname);
    // Pair-Token aus URL einsammeln (falls vorhanden) und Adressleiste putzen
    capturePairTokenFromUrl();
  }, []);

  // Vollbild-Modus: per Button im Footer oder via F-Taste umschaltbar.
  // Synct internen `isFullscreen`-State mit `document.fullscreenElement`.
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen toggle failed:', err);
    }
  };

  // Tastaturkürzel `F` schaltet Vollbild — praktisch auf TVs mit Tastatur/Funkmaus.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Footer-Auto-Hide: Maus-/Touch-/Tastatur-Aktivitaet startet einen 10 s Timer.
  // Lauft der ab, faedt der Footer aus; jede neue Aktivitaet blendet ihn sofort wieder ein.
  const showFooterAndResetTimer = () => {
    setFooterVisible(true);
    if (footerHideTimerRef.current) clearTimeout(footerHideTimerRef.current);
    footerHideTimerRef.current = setTimeout(() => setFooterVisible(false), 10000);
  };

  useEffect(() => {
    showFooterAndResetTimer();
    const handler = () => showFooterAndResetTimer();
    const events = ['mousemove', 'mousedown', 'touchstart', 'keydown'];
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      if (footerHideTimerRef.current) clearTimeout(footerHideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistenz: Scroll-Geschwindigkeit, Modus und Display-Size zwischen Sessions merken
  useEffect(() => { localStorage.setItem('tv_scroll_speed', String(scrollSpeed)); }, [scrollSpeed]);
  useEffect(() => { localStorage.setItem('tv_scroll_mode', scrollMode); }, [scrollMode]);
  useEffect(() => { localStorage.setItem('tv_display_size', displaySize); }, [displaySize]);
  useEffect(() => { localStorage.setItem('tv_columns_mode', columnsMode); }, [columnsMode]);

  // Zoom-Faktor je Display-Size — wird via inline-style auf den Schedule-Grid
  // angewendet. CSS `zoom` ist Chromium/Silk/WebKit-kompatibel.
  const DISPLAY_ZOOM = { s: 0.85, m: 1, l: 1.25, xl: 1.5 };
  const isAutoFit = displaySize === 'auto';
  const zoomFactor = isAutoFit ? autoZoom : (DISPLAY_ZOOM[displaySize] || 1);

  // Auto-Fit-Berechnung: schrumpft den Grid solange, bis er ohne Scrollen
  // in den Container passt. Konvergiert in 1-2 Iterationen, da scrollHeight
  // bereits mit dem aktuellen Zoom skaliert ist.
  useLayoutEffect(() => {
    if (!isAutoFit) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const recompute = () => {
      const c = scrollContainerRef.current;
      if (!c) return;
      const available = c.clientHeight;
      const contentScaled = c.scrollHeight;
      if (!available || !contentScaled) return;
      // Intrinsische Hoehe = scrollHeight / aktueller Zoom.
      const intrinsic = contentScaled / Math.max(0.0001, autoZoom);
      // Niemals VERGROESSERN — Auto-Fit reduziert nur, wenn ueberlaufend.
      let z = Math.min(1, available / intrinsic);
      // Untergrenze: nicht unter 0.4 (unleserlich).
      z = Math.max(0.4, z);
      if (Math.abs(z - autoZoom) > 0.01) setAutoZoom(z);
    };
    // Initial + bei Container- oder Window-Resize neu rechnen.
    const ro = new ResizeObserver(recompute);
    ro.observe(container);
    // Auch beim Mount triggern (clientHeight kennt der RO erst nach erstem Layout).
    recompute();
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
    // data, footerVisible: triggern Recompute bei Inhalts-/Layout-Aenderungen.
  }, [isAutoFit, autoZoom, data, footerVisible]);

  const loadData = async () => {
    try {
      // Geraete-Token (falls vorhanden) als Header mitsenden — Backend
      // akzeptiert TV-Aufrufe NUR mit gueltigem Token, sobald die
      // Verify-Dependency an den Endpoints aktiv ist.
      const deviceToken = localStorage.getItem('device_token');
      const headers = deviceToken ? { 'X-Device-Token': deviceToken } : {};
      const [{ data: scheduleData }, versionRes] = await Promise.all([
        axios.get(`${API_BASE}/einsatzplan/tv/${locationId}`, { headers }),
        axios.get(`${API_BASE}/einsatzplan/tv/${locationId}/version`, { headers }).catch(() => null),
      ]);
      setData(scheduleData);
      if (versionRes?.data?.version) {
        lastVersionRef.current = versionRes.data.version;
      }
      setError(null);
      setDeviceUnauthorized(false);
      setLiveSyncAt(new Date());
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isDeviceError = err?.response?.status === 403 &&
        typeof detail === 'string' && detail.toLowerCase().includes('gerät');
      if (isDeviceError) {
        setDeviceUnauthorized(true);
        setError(detail);
      } else {
        setError('Standort nicht gefunden oder keine Daten verfügbar');
      }
    } finally {
      setLoading(false);
    }
  };

  // Pause Auto-Scroll on user interaction
  const handleUserScroll = () => {
    if (autoScrollEnabled) {
      // Kurz pausieren bei manueller Interaktion
      setAutoScrollEnabled(false);
      setTimeout(() => setAutoScrollEnabled(true), 10000); // Nach 10 Sekunden wieder starten
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 text-gray-900 p-8 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-xl">Lade Einsatzplan...</p>
        </div>
      </div>
    );
  }

  if (deviceUnauthorized) {
    return <DeviceUnauthorized reason={error} />;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-stone-50 text-gray-900 p-8 flex items-center justify-center">
        <div className="text-center">
          <MapPin className="h-16 w-16 mx-auto mb-4 text-destructive" />
          <p className="text-2xl font-bold mb-2">Fehler</p>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));

  // Nur Maßnahmen der aktuellen Woche anzeigen: keine inaktiven, keine
  // ausgelaufenen / noch-nicht-gestarteten und keine für diese KW
  // ausgeschlossenen Maßnahmen.
  const tvWeekKey = `${getISOWeekYear(weekStart)}-W${String(getISOWeek(weekStart)).padStart(2, '0')}`;
  const tvWeekStartIso = format(weekStart, 'yyyy-MM-dd');
  const tvWeekEndIso = format(addDays(weekStart, 6), 'yyyy-MM-dd');
  const visibleMassnahmen = (data.massnahmen || []).filter((m) => {
    if (m.is_active === false) return false;
    if (m.end_date && m.end_date < tvWeekStartIso) return false;
    if (m.start_date && m.start_date > tvWeekEndIso) return false;
    if (Array.isArray(m.scheduled_weeks) && m.scheduled_weeks.length > 0) return m.scheduled_weeks.includes(tvWeekKey);
    if (Array.isArray(m.excluded_weeks) && m.excluded_weeks.includes(tvWeekKey)) return false;
    return true;
  });

  // Feiertage werden vom Backend automatisch fuer das Bundesland des Standorts
  // geliefert. Wir indexen sie hier per Datum fuer schnellen Lookup beim
  // Rendern der Spalten/Zellen.
  const holidayMap = (data.holidays || []).reduce((acc, h) => {
    if (h?.date) acc[h.date] = h;
    return acc;
  }, {});

  // Group shifts by massnahme and day
  const getShiftsForMassnahmeAndDay = (massnahmeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return data.shifts.filter(s => s.massnahme_id === massnahmeId && s.date === dateStr);
  };

  // Get activities for massnahme and day
  const getActivitiesForMassnahmeAndDay = (massnahmeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return (data.activities || []).filter(a => a.massnahme_id === massnahmeId && a.date === dateStr);
  };

  // Balancierte Aufteilung der Massnahmen auf zwei Spalten:
  // - Gewicht pro Massnahme = max. Anzahl Eintraege (Shifts + Aktivitaeten) an
  //   irgendeinem Wochentag. Dieser Wert entspricht der tatsaechlichen Zeilenhoehe
  //   (Items in einer Zelle sind vertikal gestapelt -> Row-Height = max ueber alle Tage).
  // - Greedy "Longest Processing Time first" (LPT): sortiere Massnahmen absteigend
  //   nach Gewicht, weise jede der aktuell weniger ausgelasteten Spalte zu.
  // - Innerhalb jeder Spalte wird die urspruengliche Reihenfolge (sort_order)
  //   beibehalten, damit der Anwender die gewohnte Struktur wiedererkennt.
  const splitMassnahmenBalanced = (list) => {
    if (!list || list.length < 2) return [list || []];
    const enriched = list.map((m, origIndex) => {
      let maxItems = 1; // mindestens 1, damit leere Massnahmen ein bisschen Gewicht haben
      for (const day of weekDays) {
        const s = getShiftsForMassnahmeAndDay(m.id, day).length;
        const a = getActivitiesForMassnahmeAndDay(m.id, day).length;
        const total = s + a;
        if (total > maxItems) maxItems = total;
      }
      return { m, origIndex, weight: maxItems };
    });
    // LPT-Greedy: groesste zuerst, in die Spalte mit aktuell kleinerem Gesamtgewicht
    const sortedByWeight = [...enriched].sort((a, b) => b.weight - a.weight || a.origIndex - b.origIndex);
    const bins = [[], []];
    const sums = [0, 0];
    for (const item of sortedByWeight) {
      const target = sums[0] <= sums[1] ? 0 : 1;
      bins[target].push(item);
      sums[target] += item.weight;
    }
    // Innerhalb jeder Spalte: nach origIndex sortieren (=> originale Reihenfolge)
    return bins.map(bin => bin.sort((a, b) => a.origIndex - b.origIndex).map(x => x.m));
  };

  return (
    <div
      className="h-screen flex flex-col bg-stone-50 text-gray-900 overflow-hidden relative"
      style={{
        // Burn-In-Schutz: minimal verschobener Inhalt, weicher Übergang.
        transform: `translate(${burnInOffset.x}px, ${burnInOffset.y}px)`,
        transition: 'transform 2s ease-in-out',
      }}
    >
      {/* Header — schmal gehalten, damit der Einsatzplan moeglichst viel
          Bildflaeche bekommt. Corporate-Look mit Logo + Primary-Akzent. */}
      <div className="flex-shrink-0 bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-[1920px] mx-auto px-4 lg:px-6 py-2 lg:py-3">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img
                src={companyLogo || '/icon.svg'}
                alt="Firmenlogo"
                className="w-16 h-16 lg:w-20 lg:h-20 shrink-0 rounded-lg object-contain bg-white"
                onError={(e) => { e.currentTarget.src = '/icon.svg'; }}
                data-testid="tv-company-logo"
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg lg:text-2xl font-bold truncate leading-tight text-gray-900">
                  {data.location.name}
                </h1>
                {data.branch && (
                  <p className="text-gray-500 flex items-center gap-1.5 text-xs lg:text-sm truncate leading-tight">
                    <Building className="h-3 w-3 lg:h-3.5 lg:w-3.5 shrink-0" />
                    <span className="truncate">{data.branch.name}</span>
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 lg:gap-4 shrink-0">
              <div className="text-right">
                <p className="text-gray-500 text-xs lg:text-sm flex items-center gap-1.5 justify-end leading-tight">
                  <Calendar className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
                  KW {data.week_number} · {format(new Date(data.week_start), 'dd.MM.', { locale: de })} – {format(new Date(data.week_end), 'dd.MM.yyyy', { locale: de })}
                </p>
                <p className="text-xl lg:text-3xl font-mono font-bold text-primary flex items-center gap-1.5 justify-end leading-tight" data-testid="tv-live-indicator">
                  <Clock className="h-4 w-4 lg:h-5 lg:w-5" />
                  <LiveClock />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Grid with Auto-Scroll */}
      <div 
        ref={scrollContainerRef}
        onWheel={handleUserScroll}
        onTouchStart={handleUserScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ zoom: zoomFactor, willChange: 'scroll-position', transform: 'translateZ(0)' }}
      >
        {visibleMassnahmen.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="h-20 w-20 mx-auto mb-6 text-gray-600" />
            <p className="text-2xl font-medium text-gray-400">Keine Maßnahmen in dieser Woche</p>
          </div>
        ) : (
              <div className={(columnsMode === '2' && visibleMassnahmen.length >= 2) ? 'flex gap-3 items-start' : ''}>
                {((columnsMode === '2' && visibleMassnahmen.length >= 2)
                  ? splitMassnahmenBalanced(visibleMassnahmen)
                  : [visibleMassnahmen]
                ).map((massnahmenSubset, _colIdx) => (
          <table key={_colIdx} className={(columnsMode === '2' && visibleMassnahmen.length >= 2) ? 'flex-1 min-w-0 table-fixed' : 'w-full table-fixed'}>
            <thead className="sticky top-0 z-20 bg-stone-100">
              <tr className="border-b border-stone-200">
                <th className="p-2 text-left font-semibold text-sm sm:text-base w-[18%] min-w-0 text-gray-700 bg-stone-100">Maßnahme</th>
                {weekDays.map(day => {
                  const dayKey = format(day, 'yyyy-MM-dd');
                  const isToday = dayKey === format(new Date(), 'yyyy-MM-dd');
                  const holiday = holidayMap[dayKey];
                  return (
                    <th key={day.toISOString()} className={`p-1.5 sm:p-2 text-center font-semibold w-[16.4%] min-w-0 ${holiday ? 'bg-red-50' : isToday ? 'bg-primary/10' : 'bg-stone-100'}`}>
                      <div className={`text-[11px] sm:text-xs ${holiday ? 'text-red-700' : isToday ? 'text-primary' : 'text-gray-500'}`}>
                        {format(day, 'EEEE', { locale: de })}
                      </div>
                      <div className={`text-base sm:text-lg leading-tight ${holiday ? 'text-red-700 font-bold' : isToday ? 'text-primary font-bold' : 'text-gray-800'}`}>
                        {format(day, 'dd.MM.', { locale: de })}
                      </div>
                      {holiday && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 rounded-md bg-red-100 text-red-800 border border-red-200 font-medium max-w-full"
                          title={`Feiertag: ${holiday.name}`}
                          data-testid={`tv-holiday-${dayKey}`}
                        >
                          <span aria-hidden="true">★</span>
                          <span className="truncate">{holiday.name}</span>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {massnahmenSubset.map(massnahme => (
                <tr key={massnahme.id} className="border-b border-stone-200 hover:bg-stone-100/60 transition-colors">
                  <td className="p-2 align-top">
                    <div className="flex items-start gap-2 min-w-0">
                      <div
                        className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-sm"
                        style={{ backgroundColor: massnahme.color }}
                      >
                        {massnahme.short_code.slice(0, 3)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm sm:text-base truncate text-gray-900 leading-tight">{massnahme.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap leading-tight">
                          <span className="truncate">{massnahme.short_code}</span>
                          {massnahme.teilnehmer_anzahl && (
                            <span className="flex items-center gap-1 shrink-0">
                              <Users className="h-3 w-3" />
                              {massnahme.teilnehmer_anzahl} TN
                            </span>
                          )}
                          {(massnahme.start_time || massnahme.end_time) && (
                            <span className="flex items-center gap-1 shrink-0 text-primary">
                              <Clock className="h-3 w-3" />
                              {(massnahme.start_time || '—').slice(0, 5)}–{(massnahme.end_time || '—').slice(0, 5)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {weekDays.map(day => {
                    const dayShifts = getShiftsForMassnahmeAndDay(massnahme.id, day);
                    const dayActivities = getActivitiesForMassnahmeAndDay(massnahme.id, day);
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const isToday = dayKey === format(new Date(), 'yyyy-MM-dd');
                    const holiday = holidayMap[dayKey];

                    // Aktivitaeten und Shifts in eine kombinierte Liste mergen,
                    // sortiert nach sort_order (gleiche Reihenfolge wie in der
                    // internen Planungsansicht). Items ohne sort_order landen
                    // ans Ende. Bei gleichem sort_order kommen Aktivitaeten zuerst
                    // (Anzeige-Konvention).
                    const cellItems = [
                      ...dayActivities.map(a => ({ ...a, _type: 'activity' })),
                      ...dayShifts.map(s => ({ ...s, _type: 'shift' })),
                    ].sort((a, b) => {
                      const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
                      const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
                      if (ao !== bo) return ao - bo;
                      if (a._type !== b._type) return a._type === 'activity' ? -1 : 1;
                      return (a.created_at || '').localeCompare(b.created_at || '');
                    });

                    return (
                      <td key={day.toISOString()} className={`p-1.5 align-top ${holiday ? 'bg-red-50/70' : isToday ? 'bg-primary/5' : ''}`}>
                        <div className="min-h-[52px] space-y-1">
                          {cellItems.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-gray-300 text-sm">
                              —
                            </div>
                          ) : (
                            cellItems.map(item => {
                              if (item._type === 'activity') {
                                const activity = item;
                                const style = ACTIVITY_STYLES[activity.activity_type] || ACTIVITY_STYLES.sonstiges;
                                const IconComponent = style.icon;
                                return (
                                  <div
                                    key={`act-${activity.id}`}
                                    className="px-1.5 py-1 rounded-md border transition-transform hover:scale-[1.02] min-w-0"
                                    style={{
                                      backgroundColor: `${style.color}15`,
                                      borderColor: `${style.color}50`
                                    }}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <div
                                        className="w-5 h-5 shrink-0 rounded-full flex items-center justify-center"
                                        style={{ backgroundColor: style.color }}
                                      >
                                        <IconComponent className="h-3 w-3 text-white" />
                                      </div>
                                      <span className="font-medium text-xs truncate" style={{ color: style.color }}>
                                        {activity.activity_label || style.label}
                                      </span>
                                      {activity.participant_count && (
                                        <span className="text-gray-600 text-[10px] flex items-center gap-0.5 shrink-0 ml-auto">
                                          <Users className="h-2.5 w-2.5" />
                                          {activity.participant_count}
                                        </span>
                                      )}
                                    </div>
                                    {activity.notes && (
                                      <div className="text-gray-500 text-[10px] mt-0.5 truncate leading-tight">
                                        {activity.notes}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              // Shift
                              const shift = item;
                              const fallbackShort = shift.employee_name
                                ? `${shift.employee_name.split(' ')[0]?.[0] || ''}. ${shift.employee_name.split(' ').slice(1).join(' ')}`.trim()
                                : '';
                              const displayName = shift.display_name || fallbackShort;
                              const initials = (shift.employee_name || '')
                                .split(' ')
                                .map(n => n[0])
                                .filter(Boolean)
                                .join('');
                              return (
                                <div
                                  key={`shift-${shift.id}`}
                                  className="px-1.5 py-1 rounded-md bg-white border border-stone-200 shadow-sm transition-transform hover:scale-[1.02] min-w-0"
                                >
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <div
                                      className="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[9px] font-medium text-white"
                                      style={{ backgroundColor: massnahme.color }}
                                    >
                                      {initials}
                                    </div>
                                    <span className="font-medium text-xs truncate text-gray-900" title={shift.employee_name || ''}>{displayName}</span>
                                    <span className="text-gray-600 text-[10px] flex items-center gap-0.5 shrink-0 ml-auto">
                                      <Clock className="h-2.5 w-2.5" />
                                      {shift.start_time && shift.end_time ? `${shift.start_time.slice(0,5)}–${shift.end_time.slice(0,5)}` : 'Ganzt.'}
                                    </span>
                                  </div>
                                  {shift.room_name && (
                                    <div className="text-gray-500 text-[10px] mt-0.5 flex items-center gap-0.5 truncate leading-tight">
                                      <DoorOpen className="h-2.5 w-2.5 shrink-0" />
                                      <span className="truncate">{shift.room_name}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
                ))}
              </div>
        )}
        {/* Spacer for footer */}
        <div className="h-16"></div>
      </div>

      {/* Show-Footer-Pfeil — bleibt am unteren Bildschirmrand sichtbar wenn der
          Footer eingeklappt ist (insbesondere fuer Touch-Geraete ohne Maus). */}
      <button
        onClick={showFooterAndResetTimer}
        className={`absolute bottom-2 left-1/2 -translate-x-1/2 z-40 bg-white/80 hover:bg-white border border-stone-300 backdrop-blur-sm rounded-full p-1.5 shadow-md transition-opacity duration-500 ${
          footerVisible ? 'opacity-0 pointer-events-none' : 'opacity-80 hover:opacity-100'
        }`}
        title="Bedienleiste einblenden"
        aria-label="Bedienleiste einblenden"
        data-testid="tv-footer-show-handle"
      >
        <ChevronUp className="h-5 w-5 text-gray-600" />
      </button>

      {/* Footer with Auto-Scroll Controls — overlay am unteren Rand, blendet sich
          nach 10s Inaktivität nach unten weg, damit der Schedule maximal Platz hat. */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-t border-stone-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] p-3 transition-transform duration-500 ${
          footerVisible ? 'translate-y-0' : 'translate-y-full pointer-events-none'
        }`}
        data-testid="tv-footer"
      >
        <div className="max-w-[1920px] mx-auto flex items-center justify-between text-sm text-gray-600">
          <span className="text-gray-500">Einsatzplan · {data.location.name}</span>

          {/* Auto-Scroll Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setAutoScrollEnabled(!autoScrollEnabled)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                autoScrollEnabled
                  ? 'bg-primary/15 text-primary'
                  : 'bg-stone-100 text-gray-600 hover:bg-stone-200'
              }`}
            >
              {autoScrollEnabled ? (
                <>
                  <Pause className="h-4 w-4" />
                  <span>Auto-Scroll an</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  <span>Auto-Scroll aus</span>
                </>
              )}
            </button>

            {autoScrollEnabled && (
              <>
                <button
                  onClick={() => setScrollMode(scrollMode === 'loop' ? 'pingpong' : 'loop')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-stone-100 text-gray-700 hover:bg-stone-200 transition-colors"
                  title={scrollMode === 'loop'
                    ? 'Aktuell: Endlos-Loop (nach unten zurück zum Anfang)'
                    : 'Aktuell: Ping-Pong (auf und ab)'}
                  data-testid="tv-scroll-mode-toggle"
                >
                  {scrollMode === 'loop' ? (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      <span className="text-xs">Loop</span>
                    </>
                  ) : (
                    <>
                      <ChevronUp className="h-3 w-3" />
                      <ChevronDown className="h-3 w-3 -ml-1" />
                      <span className="text-xs">Ping-Pong</span>
                    </>
                  )}
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs">Geschwindigkeit:</span>
                  <button
                    onClick={() => setScrollSpeed(Math.max(10, scrollSpeed - 10))}
                    className="p-1 rounded bg-stone-100 hover:bg-stone-200"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <span className="w-8 text-center text-xs">{scrollSpeed}</span>
                  <button
                    onClick={() => setScrollSpeed(Math.min(100, scrollSpeed + 10))}
                    className="p-1 rounded bg-stone-100 hover:bg-stone-200"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                </div>
              </>
            )}

            {/* Spalten-Modus Toggle (1/2 Spalten) — bei vielen Massnahmen sinnvoll,
                damit der Auto-Fit pro Spalte mehr Platz hat und alles lesbar bleibt. */}
            <div
              className="flex items-center gap-1 bg-stone-100 rounded-lg p-1"
              title="Spalten-Modus (bei vielen Massnahmen 2 Spalten waehlen)"
              data-testid="tv-columns-mode-group"
            >
              <span className="text-[10px] text-gray-500 px-1.5">Spalten:</span>
              {['1', '2'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setColumnsMode(mode)}
                  className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                    columnsMode === mode
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-stone-200'
                  }`}
                  data-testid={`tv-columns-mode-${mode}`}
                  title={mode === '2' ? 'Zwei-Spalten-Ansicht (Maßnahmen halbiert)' : 'Eine Spalte (Standard)'}
                >
                  {mode}
                </button>
              ))}
            </div>

            {/* Display-Size Toggle (AUTO/S/M/L/XL) — auch bei deaktiviertem Auto-Scroll sichtbar,
                da TV-Anzeige aus Distanz auch bei gepausten Stream funktionieren muss.
                AUTO: schrumpft den Grid automatisch, sodass alle Massnahmen ohne Scrollen passen. */}
            <div
              className="flex items-center gap-1 bg-stone-100 rounded-lg p-1"
              title="Anzeige-Größe (AUTO = alles auf einen Blick, S/M/L/XL = manuell)"
              data-testid="tv-display-size-group"
            >
              <span className="text-[10px] text-gray-500 px-1.5">Größe:</span>
              {['auto', 's', 'm', 'l', 'xl'].map((size) => (
                <button
                  key={size}
                  onClick={() => setDisplaySize(size)}
                  className={`px-2 py-1 rounded text-xs font-bold uppercase transition-colors ${
                    displaySize === size
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-stone-200'
                  }`}
                  data-testid={`tv-display-size-${size}`}
                  title={size === 'auto' ? `Auto-Fit (aktuell: ${Math.round(autoZoom * 100)} %)` : undefined}
                >
                  {size}
                </button>
              ))}
            </div>

            {/* Vollbild-Toggle — perfekt für Schulungsraum-/Empfangshallen-TVs.
                Auch via Taste `F` umschaltbar. */}
            <button
              onClick={toggleFullscreen}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-stone-100 text-gray-700 hover:bg-stone-200 transition-colors"
              title={isFullscreen ? 'Vollbild beenden (Esc oder F)' : 'Vollbild starten (F)'}
              data-testid="tv-fullscreen-toggle"
            >
              {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              <span className="text-xs">{isFullscreen ? 'Verkleinern' : 'Vollbild'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
